import React, { useState, useEffect } from 'react';
import { Card, CardHeader, Badge } from '../../../components/ui';
import {
  Shield, Users, Lock, RefreshCw, XCircle,
  CheckCircle, AlertTriangle, UserX
} from 'lucide-react';
import { adminService, SeguridadUsuario, SeguridadResumen } from '../services/adminService';

/**
 * SeguridadPage — Control de accesos y seguridad del sistema.
 * 
 * ADMIN ve: usuarios por rol, estados, accesos bloqueados.
 * ❌ NO ve datos financieros ni operativos.
 */
export const SeguridadPage: React.FC = () => {
  const [usuarios, setUsuarios] = useState<SeguridadUsuario[]>([]);
  const [resumen, setResumen] = useState<SeguridadResumen | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filtroRol, setFiltroRol] = useState('TODOS');

  const fetchData = () => {
    setLoading(true);
    setError('');
    Promise.all([
      adminService.getSeguridadUsuarios(),
      adminService.getSeguridadResumen(),
    ])
      .then(([u, r]) => { setUsuarios(u); setResumen(r); })
      .catch(err => setError(err.message || 'Error cargando datos'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchData(); }, []);

  const rolBadge = (role: string) => {
    switch (role) {
      case 'ADMIN': return <Badge variant="primary">ADMIN</Badge>;
      case 'BANCA': return <Badge variant="success">BANCA</Badge>;
      case 'LOCAL': return <Badge variant="warning">LOCAL</Badge>;
      case 'VENDEDOR_CON_CAJA': return <Badge variant="secondary">V.CAJA</Badge>;
      case 'VENDEDOR_SIN_CAJA': return <Badge variant="secondary">V.SIN</Badge>;
      default: return <Badge variant="secondary">{role}</Badge>;
    }
  };

  const usuariosFiltrados = filtroRol === 'TODOS'
    ? usuarios
    : usuarios.filter(u => u.role === filtroRol);

  const rolesUnicos = [...new Set(usuarios.map(u => u.role))];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Seguridad y Accesos</h1>
          <p className="text-gray-500">Control de usuarios y permisos del sistema</p>
        </div>
        <button
          onClick={fetchData}
          className="p-2 text-gray-500 hover:text-primary-600 hover:bg-primary-50 rounded-lg transition-colors"
          title="Refrescar"
        >
          <RefreshCw size={20} className={loading ? 'animate-spin' : ''} />
        </button>
      </div>

      {error && (
        <div className="bg-danger-50 border border-danger-200 rounded-xl p-4">
          <div className="flex items-center gap-3">
            <XCircle className="text-danger-600" size={20} />
            <p className="text-danger-800">{error}</p>
          </div>
        </div>
      )}

      {/* Resumen de seguridad */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        <Card>
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-primary-100 rounded-xl flex items-center justify-center">
              <Users className="text-primary-600" size={24} />
            </div>
            <div>
              <p className="text-sm text-gray-500">Total Usuarios</p>
              <p className="text-2xl font-bold">{resumen?.total_usuarios ?? '—'}</p>
            </div>
          </div>
        </Card>
        <Card>
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-danger-100 rounded-xl flex items-center justify-center">
              <UserX className="text-danger-600" size={24} />
            </div>
            <div>
              <p className="text-sm text-gray-500">Bloqueados</p>
              <p className="text-2xl font-bold text-danger-600">{resumen?.total_bloqueados ?? '—'}</p>
            </div>
          </div>
        </Card>
        <Card>
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-success-100 rounded-xl flex items-center justify-center">
              <Shield className="text-success-600" size={24} />
            </div>
            <div>
              <p className="text-sm text-gray-500">Roles</p>
              <p className="text-2xl font-bold">{resumen?.por_rol.length ?? '—'}</p>
            </div>
          </div>
        </Card>
      </div>

      {/* Distribución por rol */}
      <Card>
        <CardHeader title="Distribución por Rol" subtitle="Usuarios activos vs bloqueados por rol" />
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
          {resumen?.por_rol.map(r => (
            <div key={r.role} className="p-4 bg-gray-50 rounded-lg">
              <div className="flex items-center justify-between mb-2">
                {rolBadge(r.role)}
                <span className="text-lg font-bold">{r.total}</span>
              </div>
              <div className="flex items-center gap-4 text-sm">
                <span className="flex items-center gap-1 text-success-600">
                  <CheckCircle size={14} /> {r.activos} activos
                </span>
                <span className="flex items-center gap-1 text-danger-600">
                  <XCircle size={14} /> {r.total - r.activos} bloqueados
                </span>
              </div>
            </div>
          ))}
        </div>
      </Card>

      {/* Tabla de usuarios */}
      <Card>
        <CardHeader
          title="Usuarios del Sistema"
          subtitle={`${usuariosFiltrados.length} de ${usuarios.length} usuarios`}
          action={
            <select
              className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
              value={filtroRol}
              onChange={e => setFiltroRol(e.target.value)}
            >
              <option value="TODOS">Todos los roles</option>
              {rolesUnicos.map(r => (
                <option key={r} value={r}>{r}</option>
              ))}
            </select>
          }
        />
        {loading ? (
          <div className="flex justify-center py-8">
            <RefreshCw className="animate-spin text-gray-400" size={32} />
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-gray-50">
                  <th className="text-left p-3 font-medium text-gray-600">Usuario</th>
                  <th className="text-left p-3 font-medium text-gray-600">Nombre</th>
                  <th className="text-left p-3 font-medium text-gray-600">Rol</th>
                  <th className="text-left p-3 font-medium text-gray-600">Banca</th>
                  <th className="text-center p-3 font-medium text-gray-600">Estado</th>
                </tr>
              </thead>
              <tbody>
                {usuariosFiltrados.map(u => (
                  <tr key={u.id} className="border-b hover:bg-gray-50">
                    <td className="p-3">
                      <p className="font-medium">{u.username}</p>
                      <p className="text-xs text-gray-400">{u.id}</p>
                    </td>
                    <td className="p-3 text-gray-700">{u.nombre}</td>
                    <td className="p-3">{rolBadge(u.role)}</td>
                    <td className="p-3 text-gray-600 text-xs">{u.banca_nombre}</td>
                    <td className="p-3 text-center">
                      {u.activo ? (
                        <Badge variant="success">Activo</Badge>
                      ) : (
                        <Badge variant="danger">Bloqueado</Badge>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Info box */}
      <Card className="border-l-4 border-l-warning-500">
        <div className="flex items-start gap-3">
          <Lock className="text-warning-600 mt-0.5" size={20} />
          <div>
            <p className="font-semibold text-gray-800">Políticas de Seguridad</p>
            <ul className="text-sm text-gray-600 mt-1 space-y-1">
              <li>• ADMIN puede bloquear/desbloquear usuarios desde la sección Usuarios</li>
              <li>• ADMIN puede resetear contraseñas de cualquier usuario</li>
              <li>• Banca VENCIDA → todos sus usuarios se bloquean automáticamente</li>
              <li>• ADMIN NO puede acceder a operaciones del negocio</li>
            </ul>
          </div>
        </div>
      </Card>
    </div>
  );
};

export default SeguridadPage;
